// src/components/SignInForm.jsx
//
// The right column of the login page. A bare vertical form — no card,
// no shadow, no border around the group. Just the fields, an inline
// status line that updates on attempt, and the action button.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import FormField from "./FormField.jsx";
import PinInput from "./PinInput.jsx";
import TotpRing from "./TotpRing.jsx";
import { postJson } from "../lib/api.js";

const MAX_ATTEMPTS_DEFAULT = 3;
const COOLDOWN_MIN_DEFAULT = 15;

function StatusLine({ kind, children }) {
  if (!children) return null;
  const tone =
    kind === "fail"
      ? "text-aught-fail"
      : kind === "warn"
      ? "text-aught-warn"
      : kind === "ok"
      ? "text-aught-oxideDeep"
      : "text-aught-ash";
  return (
    <p
      role={kind === "fail" ? "alert" : "status"}
      className={`font-serif text-base ${tone}`}
    >
      {children}
    </p>
  );
}

export default function SignInForm({
  initialRemaining = MAX_ATTEMPTS_DEFAULT,
  initialLastAttemptAt = null,
  cooldownMin = COOLDOWN_MIN_DEFAULT,
}) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [totp, setTotp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [remaining, setRemaining] = useState(initialRemaining);
  const [lastAttemptAt, setLastAttemptAt] = useState(initialLastAttemptAt);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    const res = await postJson("/login", {
      username: username.trim(),
      password,
      pin,
      totp,
    });

    setSubmitting(false);
    const ts = Date.now();
    setLastAttemptAt(ts);

    if (res.ok && res.body?.ok) {
      navigate(res.body.redirect || "/dashboard", { replace: true });
      return;
    }

    if (res.status === 423) {
      // The parent (Login page) will detect lockout from /api/lockout and
      // swap to the closed state. We still surface the inline message here.
      setError({ kind: "fail", message: "Too many attempts. Lobby 01 is closed." });
      return;
    }

    if (res.status === 401) {
      const left = res.body?.remainingAttempts ?? Math.max(0, remaining - 1);
      setRemaining(left);
      setError({
        kind: "warn",
        message:
          left > 0
            ? `One or more keys didn't match. ${left} attempt${left === 1 ? "" : "s"} remaining before the lobby closes for ${cooldownMin} minutes.`
            : "One or more keys didn't match. The lobby is about to close.",
      });
      return;
    }

    setError({
      kind: "fail",
      message: res.body?.error || "Something went wrong. Try again.",
    });
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md space-y-8" noValidate>
      {/* Heading block. No eyebrow, no tracked-out all-caps. */}
      <div className="space-y-3">
        <h2 className="font-serif text-display text-aught-ink tracking-tightest">
          Sign in.
        </h2>
        <p className="font-serif text-lg text-aught-ash">
          Four keys are required.
        </p>
      </div>

      {/* The four fields. Generous spacing, no box chrome. */}
      <div className="space-y-7">
        <FormField
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
        <FormField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="off"
        />
        <PinInput
          label="PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        <FormField
          label="2FA code"
          value={totp}
          onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          maxLength={6}
          rightSlot={<TotpRing />}
        />
      </div>

      {/* Live inline status. This is the only place status surfaces — no
          banner, no toast, no popup. */}
      <div className="min-h-[2.25rem]">
        <StatusLine kind={error?.kind}>
          {error?.message || (
            <span className="font-serif italic text-aught-ash/80">
              {remaining > 0
                ? `${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`
                : "Awaiting a fresh 2FA code."}
            </span>
          )}
        </StatusLine>
      </div>

      {/* Primary action. Solid accent, white text, full-width. No gradient. */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-aught-oxide hover:bg-aught-oxideDeep disabled:bg-aught-ash text-aught-oxideInk font-mono text-sm tracking-[0.18em] uppercase py-4 transition-colors duration-200 ease-out-soft"
      >
        {submitting ? "Opening…" : "Open the door →"}
      </button>

      {/* Tiny footer — no icons, no SOC-2 line. */}
      <p className="font-mono text-[11px] text-aught-ash tabular pt-2">
        aught.app · build {import.meta.env.VITE_BUILD_SHA || "dev"}
      </p>
    </form>
  );
}
